import * as COSMod from 'cos-js-sdk-v5'
import { getCosCredential, type CosCredentialPayload } from './cos-repo'

type CosSdk = any

const COSAny: any = (COSMod as any)?.default ?? (COSMod as any)
const COS_SLICE_UPLOAD_THRESHOLD = 8 * 1024 * 1024
const COS_SLICE_CHUNK_SIZE = 8 * 1024 * 1024
const COS_CHUNK_PARALLEL_LIMIT = 4
const COS_FILE_PARALLEL_LIMIT = 6
const COS_SMALL_FILE_BATCH_CONCURRENCY = 8
const COS_REQUEST_TIMEOUT_MS = 120_000

type NormalizedCred = {
  bucket: string
  region: string
  secretId: string
  secretKey: string
  sessionToken: string
  startTimeSec: number
  expiredTimeSec: number
}

let cached: { cred: NormalizedCred; cos: CosSdk } | null = null
let inflightClientPromise: Promise<{ cos: CosSdk; cred: NormalizedCred }> | null = null

export type CosObjectBlobProgressDetail = {
  loadedBytes: number
  totalBytes: number | null
  progress: number | null
}

type CosObjectBlobInflightTask = {
  promise: Promise<Blob>
  detail: CosObjectBlobProgressDetail
  listeners: Set<(detail: CosObjectBlobProgressDetail) => void>
}

const cachedCosObjectBlobByKey = new Map<string, Blob>()
const cachedCosObjectBlobUrlByKey = new Map<string, string>()
const inflightCosObjectBlobByKey = new Map<string, CosObjectBlobInflightTask>()

function getBodySize(body: Blob | File | string | ArrayBuffer): number {
  try {
    if (typeof body === 'string') return new Blob([body]).size
    if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) return body.byteLength
    if (typeof Blob !== 'undefined' && body instanceof Blob) return body.size
  } catch { /* noop */ }
  return 0
}

function shouldUseSliceUpload(body: Blob | File | string | ArrayBuffer): boolean {
  try {
    return typeof Blob !== 'undefined' && body instanceof Blob && body.size >= COS_SLICE_UPLOAD_THRESHOLD
  } catch {
    return false
  }
}

function toSec(v: string | number): number {
  if (typeof v === 'number') {
    // 有的后端是秒，有的是毫秒；用一个保守判断
    return v > 10_000_000_000 ? Math.floor(v / 1000) : Math.floor(v)
  }
  const s = String(v ?? '').trim()
  if (!s) return 0
  const n = Number(s)
  if (Number.isFinite(n) && n > 0) return n > 10_000_000_000 ? Math.floor(n / 1000) : Math.floor(n)
  const t = Date.parse(s)
  return Number.isFinite(t) ? Math.floor(t / 1000) : 0
}

function normalizeCred(p: CosCredentialPayload): NormalizedCred {
  const bucket = String(p.bucket ?? '').trim()
  const region = String(p.region ?? '').trim()
  const secretKey = String((p as any).tmp_secret_key ?? '').trim()
  const sessionToken = String((p as any).session_token ?? '').trim()
  const secretId = String(
    (p as any).tmp_secret_id ??
      (p as any).tmp_secretId ??
      (p as any).tmp_secret_key_id ??
      (p as any).tmp_secretKeyId ??
      '',
  ).trim()

  const startTimeSec = toSec((p as any).start_time)
  const expiredTimeSec = toSec((p as any).expired_time)

  if (!bucket || !region || !secretId || !secretKey) {
    throw new Error('COS 临时密钥缺少必要字段（bucket/region/tmp_secret_id/tmp_secret_key）')
  }

  return { bucket, region, secretId, secretKey, sessionToken, startTimeSec, expiredTimeSec }
}

async function getCosClient(minRemainSec = 90): Promise<{ cos: CosSdk; cred: NormalizedCred }> {
  const nowSec = Math.floor(Date.now() / 1000)
  if (cached?.cred) {
    const remain = cached.cred.expiredTimeSec - nowSec
    if (remain > minRemainSec) return cached
  }

  if (inflightClientPromise) return inflightClientPromise

  inflightClientPromise = (async () => {
    const res = await getCosCredential()
    const cred = normalizeCred(res.data)
    const cos = new (COSAny as any)({
      SecretId: cred.secretId,
      SecretKey: cred.secretKey,
      SecurityToken: cred.sessionToken,
      Timeout: COS_REQUEST_TIMEOUT_MS,
      FileParallelLimit: COS_FILE_PARALLEL_LIMIT,
      ChunkParallelLimit: COS_CHUNK_PARALLEL_LIMIT,
      ChunkSize: COS_SLICE_CHUNK_SIZE,
    })
    cached = { cred, cos }
    return { cred, cos }
  })()
  try {
    return await inflightClientPromise
  } finally {
    inflightClientPromise = null
  }
}

function formatCosSdkError(err: any): string {
  try {
    const status = err?.statusCode ?? err?.status ?? err?.response?.status
    const code =
      err?.error?.Code ??
      err?.error?.code ??
      err?.Code ??
      err?.code ??
      err?.response?.data?.Code ??
      err?.response?.data?.code
    const msg =
      err?.error?.Message ??
      err?.error?.message ??
      err?.Message ??
      err?.message ??
      err?.response?.data?.Message ??
      err?.response?.data?.message
    const rid =
      err?.error?.RequestId ??
      err?.error?.requestId ??
      err?.RequestId ??
      err?.requestId ??
      err?.response?.headers?.['x-cos-request-id']
    const parts = [
      status ? `HTTP ${status}` : '',
      code ? `Code=${code}` : '',
      msg ? `Message=${msg}` : '',
      rid ? `RequestId=${rid}` : '',
    ].filter(Boolean)
    const s = parts.join(' ')
    return s || 'COS 请求失败'
  } catch {
    return 'COS 请求失败'
  }
}

export function normalizeCosKey(pathOrKey: string): string {
  const raw = String(pathOrKey ?? '').trim()
  if (!raw) return ''
  // 若传的是完整 URL，取 pathname
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw)
      return String(u.pathname ?? '').replace(/^\/+/, '')
    } catch {
      return raw.replace(/^\/+/, '')
    }
  }
  return raw.replace(/^\/+/, '')
}

export function getCosObjectBlobCacheKey(pathOrUrl: string): string {
  const raw = String(pathOrUrl ?? '').trim()
  if (!raw) return ''
  if (raw.startsWith('blob:')) return raw
  return normalizeCosKey(raw)
}

export function getCachedCosObjectBlobUrl(pathOrUrl: string): string {
  const raw = String(pathOrUrl ?? '').trim()
  if (!raw || raw.startsWith('blob:')) return raw
  const key = getCosObjectBlobCacheKey(raw)
  if (!key) return ''
  return String(cachedCosObjectBlobUrlByKey.get(key) ?? '').trim()
}

export function getCachedCosObjectBlob(pathOrUrl: string): Blob | null {
  const raw = String(pathOrUrl ?? '').trim()
  if (!raw || raw.startsWith('blob:')) return null
  const key = getCosObjectBlobCacheKey(raw)
  if (!key) return null
  const blob = cachedCosObjectBlobByKey.get(key)
  return blob instanceof Blob ? blob : null
}

export function cacheCosObjectBlob(pathOrUrl: string, blob: Blob): Blob | null {
  const raw = String(pathOrUrl ?? '').trim()
  if (!raw || !(blob instanceof Blob) || raw.startsWith('blob:')) return null
  const key = getCosObjectBlobCacheKey(raw)
  if (!key) return null
  cachedCosObjectBlobByKey.set(key, blob)
  return blob
}

export function cacheCosObjectBlobUrl(pathOrUrl: string, blob: Blob): string {
  const raw = String(pathOrUrl ?? '').trim()
  if (!raw || !(blob instanceof Blob)) return ''
  if (raw.startsWith('blob:')) return raw
  const key = getCosObjectBlobCacheKey(raw)
  if (!key) return ''
  cacheCosObjectBlob(raw, blob)
  const prev = String(cachedCosObjectBlobUrlByKey.get(key) ?? '').trim()
  if (prev && prev.startsWith('blob:')) {
    try { URL.revokeObjectURL(prev) } catch { /* noop */ }
  }
  const objectUrl = URL.createObjectURL(blob)
  cachedCosObjectBlobUrlByKey.set(key, objectUrl)
  return objectUrl
}

export async function ensureCosObjectBlobUrl(
  pathOrUrl: string,
  opts?: {
    onProgress?: (percent0to100: number) => void
    onProgressDetail?: (detail: CosObjectBlobProgressDetail) => void
  },
): Promise<string> {
  const raw = String(pathOrUrl ?? '').trim()
  if (!raw || raw.startsWith('blob:') || !/^https?:\/\//i.test(raw)) return raw

  const key = getCosObjectBlobCacheKey(raw)
  if (!key) return raw

  const cachedObjectUrl = getCachedCosObjectBlobUrl(raw)
  if (cachedObjectUrl) return cachedObjectUrl
  const cachedBlob = getCachedCosObjectBlob(raw)
  if (cachedBlob) return cacheCosObjectBlobUrl(raw, cachedBlob)
  const blob = await cosGetObjectBlobByFetch(raw, opts)
  const latestObjectUrl = getCachedCosObjectBlobUrl(raw)
  if (latestObjectUrl) return latestObjectUrl
  return cacheCosObjectBlobUrl(raw, blob)
}

function encodeKeyForUrl(key: string): string {
  return String(key ?? '')
    .split('/')
    .map(seg => encodeURIComponent(seg))
    .join('/')
}

function toHttpsCosUrl(url: unknown): string {
  const raw = String(url ?? '').trim()
  if (!raw) return raw
  if (raw.startsWith('//')) return `https:${raw}`
  if (/^http:\/\//i.test(raw)) return raw.replace(/^http:\/\//i, 'https://')
  return raw
}

export function buildCosPublicUrl(bucket: string, region: string, key: string): string {
  const k = encodeKeyForUrl(normalizeCosKey(key))
  return `https://${bucket}.cos.${region}.myqcloud.com/${k}`
}

export async function cosPutObject(params: {
  key: string
  body: Blob | File | string | ArrayBuffer
  contentType?: string
  onProgress?: (percent0to100: number) => void
  signal?: AbortSignal
}): Promise<{ key: string; url: string; etag?: string }> {
  const bodySize = getBodySize(params.body)
  const useSliceUpload = shouldUseSliceUpload(params.body)
  const minRemainSec = useSliceUpload
    ? (bodySize >= 512 * 1024 * 1024 ? 30 * 60 : bodySize >= 128 * 1024 * 1024 ? 20 * 60 : 10 * 60)
    : 90
  const { cos, cred } = await getCosClient(minRemainSec)
  const Key = normalizeCosKey(params.key)
  if (!Key) throw new Error('COS Key 不能为空')

  const emitProgress = (p: any) => {
    try {
      const percent = Math.max(0, Math.min(100, Math.round(Number(p?.percent ?? 0) * 100)))
      params.onProgress?.(percent)
    } catch { /* noop */ }
  }

  params.onProgress?.(0)

  const commonPayload = {
    Bucket: cred.bucket,
    Region: cred.region,
    Key,
    Body: params.body,
    ContentType: params.contentType,
    onProgress: emitProgress,
  } as any

  const res: any = await new Promise((resolve, reject) => {
    let aborted = false
    let taskId: any = null
    let req: any = null

    const abortError = () => {
      try { return new DOMException('Aborted', 'AbortError') } catch { return new Error('Aborted') }
    }

    const onAbort = () => {
      aborted = true
      try {
        if (taskId != null) cos.cancelTask?.(taskId)
      } catch { /* noop */ }
      try {
        req?.abort?.()
      } catch { /* noop */ }
      reject(abortError())
    }

    if (params.signal?.aborted) {
      onAbort()
      return
    }
    try { params.signal?.addEventListener('abort', onAbort, { once: true } as any) } catch { /* noop */ }

    const done = (err: any, data: any) => {
      try { params.signal?.removeEventListener?.('abort', onAbort as any) } catch { /* noop */ }
      if (aborted) return
      if (err) {
        try {
          // 高价值诊断：浏览器里 403 常见原因是 CORS/权限/临时密钥过期/region 不匹配
           
          console.warn('[COS] upload failed:', {
            bucket: cred.bucket,
            region: cred.region,
            key: Key,
            useSliceUpload,
            size: bodySize,
            err,
            hint: '若为 403：请检查临时密钥策略是否包含 PutObject/InitiateMultipartUpload/UploadPart/CompleteMultipartUpload，以及 COS Bucket 的 CORS 是否允许对应方法与头',
          })
        } catch { /* noop */ }
        reject(err)
      } else {
        resolve(data)
      }
    }

    if (useSliceUpload) {
      req = cos.sliceUploadFile(
        {
          ...commonPayload,
          SliceSize: COS_SLICE_CHUNK_SIZE,
          AsyncLimit: COS_CHUNK_PARALLEL_LIMIT,
          onTaskReady: (id: any) => {
            taskId = id
            if (aborted) {
              try { cos.cancelTask?.(taskId) } catch { /* noop */ }
            }
          },
        },
        done,
      )
      return
    }

    req = cos.putObject(commonPayload, done)
  })

  params.onProgress?.(100)

  return {
    key: Key,
    url: buildCosPublicUrl(cred.bucket, cred.region, Key),
    etag: String(res?.ETag ?? res?.etag ?? '').trim() || undefined,
  }
}

export async function cosPutObjects(params: {
  items: Array<{
    key: string
    body: Blob | File | string | ArrayBuffer
    contentType?: string
  }>
  concurrency?: number
  onItemProgress?: (index: number, percent0to100: number) => void
  signal?: AbortSignal
}): Promise<Array<{ key: string; url: string; etag?: string }>> {
  const items = Array.isArray(params.items) ? params.items : []
  if (items.length === 0) return []

  const concurrency = Math.max(1, Math.min(Number(params.concurrency ?? COS_SMALL_FILE_BATCH_CONCURRENCY) || COS_SMALL_FILE_BATCH_CONCURRENCY, 16))
  const results: Array<{ key: string; url: string; etag?: string }> = new Array(items.length)
  let nextIndex = 0

  const worker = async () => {
    while (true) {
      if (params.signal?.aborted) {
        try { throw new DOMException('Aborted', 'AbortError') } catch { throw new Error('Aborted') }
      }
      const current = nextIndex
      nextIndex += 1
      if (current >= items.length) return
      const item = items[current]
      results[current] = await cosPutObject({
        key: item.key,
        body: item.body,
        contentType: item.contentType,
        signal: params.signal,
        onProgress: (percent) => {
          try { params.onItemProgress?.(current, percent) } catch { /* noop */ }
        },
      })
      try { params.onItemProgress?.(current, 100) } catch { /* noop */ }
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, () => worker()))
  return results
}

export async function cosGetObjectText(
  pathOrKey: string,
  opts?: {
    onProgress?: (percent0to100: number) => void
  },
): Promise<string> {
  const { cos, cred } = await getCosClient()
  const Key = normalizeCosKey(pathOrKey)
  if (!Key) throw new Error('COS Key 不能为空')

  const res: any = await new Promise((resolve, reject) => {
    cos.getObject(
      {
        Bucket: cred.bucket,
        Region: cred.region,
        Key,
        // COS SDK 下载进度（若底层支持，会持续回调）
        onProgress: (p: any) => {
          try {
            let percent: number | null = null
            if (p && typeof p.percent === 'number') {
              percent = p.percent * 100
            } else if (p && typeof p.loaded === 'number' && typeof p.total === 'number' && p.total > 0) {
              percent = (p.loaded / p.total) * 100
            }
            if (percent == null || !Number.isFinite(percent)) return
            const safe = Math.max(0, Math.min(100, percent))
            opts?.onProgress?.(safe)
          } catch { /* noop */ }
        },
      },
      (err: any, data: any) => {
        if (err) {
          try {
            console.warn('[COS] getObject failed:', { bucket: cred.bucket, region: cred.region, key: Key, err })
          } catch { /* noop */ }
          reject(err)
        }
        else resolve(data)
      },
    )
  })

  const body = res?.Body
  if (typeof body === 'string') return body
  if (body instanceof ArrayBuffer) return new TextDecoder().decode(new Uint8Array(body))
  if (body && typeof body === 'object' && (body as any).buffer) {
    try {
      const u8 = body instanceof Uint8Array ? body : new Uint8Array((body as any).buffer)
      return new TextDecoder().decode(u8)
    } catch { /* noop */ }
  }
  // 兜底：COS SDK 有时会返回 Blob
  if (typeof (body as any)?.text === 'function') return await (body as any).text()
  return String(body ?? '')
}

export async function cosGetObjectTextByFetch(
  pathOrKey: string,
  opts?: {
    onProgress?: (percent0to100: number) => void
  },
): Promise<string> {
  const blob = await cosGetObjectBlobByFetch(pathOrKey, {
    onProgress: opts?.onProgress,
  })
  try {
    return await blob.text()
  } catch {
    const buffer = await blob.arrayBuffer()
    return new TextDecoder().decode(new Uint8Array(buffer))
  }
  /*
  const { cred } = await getCosClient()
  const Key = normalizeCosKey(pathOrKey)
  if (!Key) throw new Error('COS Key 不能为空')

  const url = buildCosPublicUrl(cred.bucket, cred.region, Key)
  opts?.onProgress?.(0)

  const res = await fetch(url, { method: 'GET' })
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} ${res.statusText || 'COS 请求失败'}`.trim())
  }

  if (!res.body) {
    const text = await res.text()
    opts?.onProgress?.(100)
    return text
  }

  const total = Number(res.headers.get('content-length') ?? 0)
  const reader = res.body.getReader()
  const chunks: Uint8Array[] = []
  let loaded = 0

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    const chunk = value instanceof Uint8Array ? value : new Uint8Array(value ?? [])
    if (chunk.length > 0) {
      chunks.push(chunk)
      loaded += chunk.length
      if (total > 0) {
        const percent = Math.max(0, Math.min(100, (loaded / total) * 100))
        opts?.onProgress?.(percent)
      }
    }
  }

  const merged = new Uint8Array(loaded)
  let offset = 0
  for (const chunk of chunks) {
    merged.set(chunk, offset)
    offset += chunk.length
  }
  opts?.onProgress?.(100)
  return new TextDecoder().decode(merged)
  */
}

export async function cosGetObjectBlobByFetch(
  pathOrKey: string,
  opts?: {
    onProgress?: (percent0to100: number) => void
    onProgressDetail?: (detail: {
      loadedBytes: number
      totalBytes: number | null
      progress: number | null
    }) => void
  },
): Promise<Blob> {
  const sharedRawPath = String(pathOrKey ?? '').trim()
  const sharedKey = normalizeCosKey(pathOrKey)
  if (!sharedKey) throw new Error('COS Key 不能为空')
  const sharedCacheKey = getCosObjectBlobCacheKey(sharedRawPath || sharedKey)
  const emitSharedToConsumer = (detail: CosObjectBlobProgressDetail) => {
    opts?.onProgressDetail?.(detail)
    if (detail.progress != null) {
      opts?.onProgress?.(Math.max(0, Math.min(100, detail.progress * 100)))
    }
  }

  if (sharedCacheKey) {
    const cachedBlob = cachedCosObjectBlobByKey.get(sharedCacheKey)
    if (cachedBlob instanceof Blob) {
      emitSharedToConsumer({
        loadedBytes: cachedBlob.size,
        totalBytes: cachedBlob.size,
        progress: 1,
      })
      opts?.onProgress?.(100)
      return cachedBlob
    }
    const inflight = inflightCosObjectBlobByKey.get(sharedCacheKey)
    if (inflight) {
      const listener = (detail: CosObjectBlobProgressDetail) => emitSharedToConsumer(detail)
      emitSharedToConsumer(inflight.detail)
      inflight.listeners.add(listener)
      try {
        return await inflight.promise
      } finally {
        inflight.listeners.delete(listener)
      }
    }
  }

  const { cred: sharedCred } = await getCosClient()
  const sharedUrl = /^https?:\/\//i.test(sharedRawPath) ? sharedRawPath : buildCosPublicUrl(sharedCred.bucket, sharedCred.region, sharedKey)
  const emitSharedProgress = (loadedBytes: number, totalBytes: number | null) => {
    const detail: CosObjectBlobProgressDetail = {
      loadedBytes,
      totalBytes,
      progress: totalBytes && totalBytes > 0 ? (loadedBytes / totalBytes) : null,
    }
    if (sharedCacheKey) {
      const current = inflightCosObjectBlobByKey.get(sharedCacheKey)
      if (current) current.detail = detail
      for (const listener of current?.listeners ?? []) {
        try { listener(detail) } catch { /* noop */ }
      }
    }
    emitSharedToConsumer(detail)
  }

  const sharedTask = (async () => {
    emitSharedProgress(0, null)
    const response = await fetch(sharedUrl)
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} ${response.statusText || 'COS 请求失败'}`.trim())
    }

    const totalBytesRaw = Number(response.headers.get('content-length') ?? 0)
    const totalBytes = Number.isFinite(totalBytesRaw) && totalBytesRaw > 0 ? totalBytesRaw : null
    emitSharedProgress(0, totalBytes)

    let blob: Blob
    if (!response.body) {
      blob = await response.blob()
      emitSharedProgress(blob.size, totalBytes ?? blob.size)
    } else {
      const reader = response.body.getReader()
      const chunks: Uint8Array[] = []
      let loadedBytes = 0
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        if (!value) continue
        chunks.push(value)
        loadedBytes += value.byteLength
        emitSharedProgress(loadedBytes, totalBytes)
      }
      blob = new Blob(chunks as unknown as BlobPart[])
      emitSharedProgress(blob.size, totalBytes ?? blob.size)
    }
    if (sharedCacheKey) cacheCosObjectBlob(sharedCacheKey, blob)
    opts?.onProgress?.(100)
    return blob
  })()

  if (sharedCacheKey) {
    inflightCosObjectBlobByKey.set(sharedCacheKey, {
      promise: sharedTask,
      detail: {
        loadedBytes: 0,
        totalBytes: null,
        progress: 0,
      },
      listeners: new Set(),
    })
  }

  try {
    return await sharedTask
  } finally {
    if (sharedCacheKey) inflightCosObjectBlobByKey.delete(sharedCacheKey)
  }
  /*
  const { cred } = await getCosClient()
  const rawPath = String(pathOrKey ?? '').trim()
  const Key = normalizeCosKey(pathOrKey)
  if (!Key) throw new Error('COS Key 不能为空')

  const url = /^https?:\/\//i.test(rawPath) ? rawPath : buildCosPublicUrl(cred.bucket, cred.region, Key)
  const emitProgress = (loadedBytes: number, totalBytes: number | null) => {
    const progress = totalBytes && totalBytes > 0 ? (loadedBytes / totalBytes) : null
    opts?.onProgressDetail?.({
      loadedBytes,
      totalBytes,
      progress,
    })
    if (progress != null) {
      opts?.onProgress?.(Math.max(0, Math.min(100, progress * 100)))
    }
  }

  emitProgress(0, null)
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} ${response.statusText || 'COS 请求失败'}`.trim())
  }

  const totalBytesRaw = Number(response.headers.get('content-length') ?? 0)
  const totalBytes = Number.isFinite(totalBytesRaw) && totalBytesRaw > 0 ? totalBytesRaw : null
  emitProgress(0, totalBytes)

  let blob: Blob
  if (!response.body) {
    blob = await response.blob()
    emitProgress(blob.size, totalBytes ?? blob.size)
  } else {
    const reader = response.body.getReader()
    const chunks: Uint8Array[] = []
    let loadedBytes = 0
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      if (!value) continue
      chunks.push(value)
      loadedBytes += value.byteLength
      emitProgress(loadedBytes, totalBytes)
    }
    blob = new Blob(chunks)
    emitProgress(blob.size, totalBytes ?? blob.size)
  }
  opts?.onProgress?.(100)
  return blob
  */
}

export async function getObjectUrl(key: string) {
  const { cos, cred } = await getCosClient()
  return new Promise<string>((resolve, reject) => {
    cos.getObjectUrl({
      Bucket: cred.bucket,
      Region: cred.region,
      Key: key,
      Sign: true,
      Expires: 3600,
      Protocol: 'https:',
    }, (err: any, data: any) => {
      if (err) reject(err)
      else resolve(toHttpsCosUrl(data?.Url))
    })
  })
}

// 统一对外抛出的错误信息（便于 UI message 展示）
export function cosErrorToMessage(err: any): string {
  return formatCosSdkError(err)
}

// blob 转 File
export function blobToFile(blob: Blob, fileName: string, options?: FilePropertyBag): File {
  return new File([blob], fileName, options ?? { type: blob.type })
}